import { type ComboboxData, type ComboboxItem } from "@mantine/core";
import { sortComboboxItems } from "../../../mantine/utils/sortComboboxItems";
import { FREQUENCY_CHOICES, MEDIUM_CHOICES } from "../../../utils/constants";
import { type FormInputData } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { ORIGIN_LABEL_MAP } from "../../EnergyData/common";
import type { EdaVersionFormValues } from "./CreateEdaVersionModal.types";
import {
  ESTIMATION_PROCEDURES,
  ESTIMATION_PROCEDURES_CONSTANT_LOAD,
  ESTIMATION_PROCEDURES_EXAMPLE_MEASURING,
  ESTIMATION_PROCEDURES_MISC,
  ESTIMATION_PROCEDURES_NON_CALIBRATED_MEASURING,
  ESTIMATION_PROCEDURES_PARITAL_RATIO,
  ESTIMATION_PROCEDURES_TYPICAL_STANDARD_VALUES,
  ESTIMATION_PROCEDURES_TYPICAL_STANDARD_VALUES_15_MIN
} from "./CreateEdaVersionModal.types";

export enum EDA_FORM_FIELDS {
  annualConsumption = "annualConsumption",
  artDerEnergieMenge = "artDerEnergieMenge",
  company = "company",
  connection = "connection",
  constantLoadKw = "constantLoadKw",
  consumer = "consumer",
  description = "description",
  estimationProcedure = "estimationProcedure",
  formula = "formula",
  frequency = "frequency",
  generator = "generator",
  label = "label",
  medium = "medium",
  meter = "meter",
  numberOfConsumers = "numberOfConsumers",
  origin = "origin",
  ratioOfEnergyData = "ratioOfEnergyData",
  referencedEdaIdentity = "referencedEdaIdentity",
  rootMeter = "rootMeter",
  securityBuffer = "securityBuffer",
  stromsteuerpflichtig = "stromsteuerpflichtig",
  supplied = "supplied",
  supplier = "supplier",
  tags = "tags",
  validity = "validity",
  toCompany = "toCompany",
  fromCompany = "fromCompany",
  component = "component"
}

export const INPUT_LABELS = {
  [EDA_FORM_FIELDS.component]: { label: "Verbraucher" },
  [EDA_FORM_FIELDS.description]: {
    label: "Beschreibung"
  },
  [EDA_FORM_FIELDS.toCompany]: {
    label: "Belieferte"
  },
  [EDA_FORM_FIELDS.fromCompany]: {
    label: "Lieferant"
  },
  [EDA_FORM_FIELDS.annualConsumption]: {
    label: "Jahresverbrauch [kWh/a]"
  },
  [EDA_FORM_FIELDS.artDerEnergieMenge]: {
    label: "Art der Energiemenge"
  },
  [EDA_FORM_FIELDS.company]: {
    label: "Company"
  },
  [EDA_FORM_FIELDS.connection]: {
    label: "Connection"
  },
  [EDA_FORM_FIELDS.constantLoadKw]: {
    label: "Konstante Leistung [kW]",
    helptext:
      "\nZur Bestimmung der geschätzten Energiemenge wird die Anzahl der gleichartigen \nVerbraucher mit der eingetragenen konstanten Leistung multipliziert.\n\n###### Beispiel \nWenn der Verbrauch von 5 Getränkeautomaten desselben Typs mit einer \nmaximalen Leistungsaufnahme von jeweils 0,6 kW über die Worst-Case-Schätzung \nermittelt werden soll, ist eine konstante Leistung von 0,6 kW und eine \nAnzahl gleichartiger Verbraucher von 5 einzutragen.\n"
  },
  [EDA_FORM_FIELDS.consumer]: {
    label: "Verbraucher"
  },
  [EDA_FORM_FIELDS.estimationProcedure]: {
    label: "Schätzverfahren"
  },
  [EDA_FORM_FIELDS.formula]: {
    label: "Formelausdruck als Zeichenkette",
    helptext:
      "Der Formelausdruck kann auf Daten aus anderen Energiedatenerfassungen zugreifen, um eine neue Zeitreihe berechnen zu lassen.\n\n*Wichtig*: Falls die Berechnungsformel gemischte Additionen, Subtraktionen oder Vergleiche von Zahlen und Energiedatenerfassungen enthält, müssen die Zahlenwerte in der Einheit 'Kilowatt' angegeben werden.\n\n#### Operanden\n\nFolgende Operanden stehen zur Verfügung:\n\n- Energiedatenerfassung: eda_123\n- Natürliche Zahl: 2\n- Gleitkommazahl: 0.5\n\n#### Arithmetische Operatoren\n\nFolgende *elementweise* arithmetische Operatoren stehen zur Verfügung:\n\n- Addition: eda_123 + eda_124\n- Subtraktion: eda_123 - 50\n- Multiplikation: 2.5 * eda_124\n- Division mit Zahl als Divisor: eda_124 / 2\n    - Hinweis: Eine Division mit Energiedatenerfassung als Divisor (2 / eda_124) ist nicht erlaubt, da bei\n      Division durch Null undefinierte Werte entstehen. Falls ein Anteil berechnet werden soll, kann die\n      Anteil-Funktion (s.u.) verwendet werden.\n\n#### Funktionen\n\nFolgende *elementweise* Funktionen stehen zur Verfügung:\n\n- Minimum: Min(eda_123, 0.5, eda_125, ...)\n- Maximum: Max(eda_123, eda_124, 50, ...)\n- Absolutwert: Abs(eda_123)\n- Signum: Sign(eda_123)\n- Wenn-Dann-Sonst: Wenn(Le(eda_123, 5), eda_124, 62) mit den Vergleichsoperatoren:\n  - Kleiner: Lt(eda_123, 0.2)\n  - Kleiner gleich: Le(eda_123, 0.2)\n  - Größer: Gt(eda_123, 5)\n  - Größer gleich: Ge(eda_123, 5)\n  - Gleich: Eq(eda_123, 0)\n  - Ungleich: Ne(eda_123, 0)\n- Zeitliches Wenn-Dann-Sonst: ZeitlichesWenn(2020, 5, 1, 2020, 12, 1, eda_123, 0), wobei gilt:\n  - Die ersten drei Operanden beschreiben ein Startdatum oder sind alle Null, in diesem Fall gibt es kein Startdatum\n  - Die nächsten drei Operanden beschreiben ein Enddatum oder sind alle Null, in diesem Fall gibt es kein Enddatum\n  - Innerhalb des Zeitintervalls wird der Wert des vorletzten Operanden eingesetzt (im Beispiel eda_123), außerhalb der des letzten Operanden (im Beispiel 0). Das Zeitintervall ist rechtsoffen definiert, d.h. das Intervall schließt das Startdatum ein und das Enddatum aus.\n- Ersatzwertbildung: FehlendeWerteErsetzen(eda_123, 0)\n- Frequenzumwandlung: StandardFrequenz(eda_123) wandelt eine Energiedatenerfassung in beliebiger Auflösung in Viertelstundenwerte um.\n- Anteilsberechnung: Anteil(eda_123, eda_456) berechnet in jedem Intervall den Anteil von eda_123 an der Menge von eda_456. Dabei werden die Zeitreihen elementweise durcheinander geteilt. Sollte der Nenner Null sein, wird für das entsprechende Intervall der Mittelwert in den anderen Intervallen eingesetzt.\n- Mittelwertberechnung: Mittelwerte(eda_123) berechnet den Mittelwert (Mean) der Energiedaten, wobei fehlende Werte ignoriert werden.\n"
  },
  [EDA_FORM_FIELDS.frequency]: {
    label: "Intervall der Datenpunkte"
  },
  [EDA_FORM_FIELDS.generator]: {
    label: "Erzeuger"
  },
  [EDA_FORM_FIELDS.label]: {
    label: "Ergänzung"
  },
  [EDA_FORM_FIELDS.medium]: {
    label: "Medium"
  },
  [EDA_FORM_FIELDS.meter]: {
    label: "Zähler"
  },
  [EDA_FORM_FIELDS.numberOfConsumers]: {
    label:
      "Anzahl gleichartiger Verbraucher, auf die das Schätzverfahren angewendet werden soll"
  },
  [EDA_FORM_FIELDS.origin]: {
    label: "Herkunft"
  },
  [EDA_FORM_FIELDS.ratioOfEnergyData]: {
    label: "Anteil der Schätzmenge an der zugrundeliegenden Energiemenge [%]"
  },
  [EDA_FORM_FIELDS.referencedEdaIdentity]: {
    label: "Referenzierte Energiemenge"
  },
  [EDA_FORM_FIELDS.rootMeter]: {
    label: "Root Meter"
  },
  [EDA_FORM_FIELDS.securityBuffer]: {
    label: "Sicherheitsaufschlag [%]"
  },
  [EDA_FORM_FIELDS.stromsteuerpflichtig]: {
    label: "Stromsteuerpflichtig"
  },
  [EDA_FORM_FIELDS.supplied]: {
    label: "Supplied"
  },
  [EDA_FORM_FIELDS.supplier]: {
    label: "Supplier"
  },
  [EDA_FORM_FIELDS.tags]: {
    label: "Klassifizierung"
  },
  [EDA_FORM_FIELDS.validity]: {
    label: "Gültigkeitszeitraum"
  }
} as const;

const ESTIMATION_PROCEDURE_OPTIONS: ComboboxData = Object.entries(
  ESTIMATION_PROCEDURES
)
  .map<ComboboxItem>(([key, value]) => {
    return {
      label: value,
      value: key
    };
  })
  .sort(sortComboboxItems);

const ORIGIN_OPTIONS: ComboboxData = Object.entries(ORIGIN_LABEL_MAP)
  .map<ComboboxItem>(([key, value]) => ({
    label: value,
    value: key
  }))
  .sort(sortComboboxItems)
  .filter((item) => item.label !== "Marktlokation");

const FREQUENCY_OPTIONS: ComboboxData = Object.values(
  FREQUENCY_CHOICES
).map<ComboboxItem>((v) => ({
  value: v.value,
  label: v.displayName
}));

//CreateEdaFormFields
export const CREATE_EDA_VERSION_FORM_FIELD_DATA = {
  [EDA_FORM_FIELDS.artDerEnergieMenge]: {
    name: EDA_FORM_FIELDS.artDerEnergieMenge,
    label: INPUT_LABELS[EDA_FORM_FIELDS.artDerEnergieMenge].label,
    type: "select",
    required: true
  },

  // referenzen-container fields
  [EDA_FORM_FIELDS.description]: {
    name: EDA_FORM_FIELDS.description,
    label: INPUT_LABELS[EDA_FORM_FIELDS.description].label,
    type: "text",
    required: true
  },
  [EDA_FORM_FIELDS.component]: {
    name: EDA_FORM_FIELDS.component,
    label: INPUT_LABELS[EDA_FORM_FIELDS.component].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.company]: {
    name: EDA_FORM_FIELDS.company,
    label: INPUT_LABELS[EDA_FORM_FIELDS.company].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.connection]: {
    name: EDA_FORM_FIELDS.connection,
    label: INPUT_LABELS[EDA_FORM_FIELDS.connection].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.consumer]: {
    name: EDA_FORM_FIELDS.consumer,
    label: INPUT_LABELS[EDA_FORM_FIELDS.consumer].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.generator]: {
    name: EDA_FORM_FIELDS.generator,
    label: INPUT_LABELS[EDA_FORM_FIELDS.generator].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.meter]: {
    name: EDA_FORM_FIELDS.meter,
    label: INPUT_LABELS[EDA_FORM_FIELDS.meter].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.rootMeter]: {
    name: EDA_FORM_FIELDS.rootMeter,
    label: INPUT_LABELS[EDA_FORM_FIELDS.rootMeter].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.supplied]: {
    name: EDA_FORM_FIELDS.supplied,
    label: INPUT_LABELS[EDA_FORM_FIELDS.supplied].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.supplier]: {
    name: EDA_FORM_FIELDS.supplier,
    label: INPUT_LABELS[EDA_FORM_FIELDS.supplier].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.toCompany]: {
    name: EDA_FORM_FIELDS.toCompany,
    label: INPUT_LABELS[EDA_FORM_FIELDS.toCompany].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.fromCompany]: {
    name: EDA_FORM_FIELDS.fromCompany,
    label: INPUT_LABELS[EDA_FORM_FIELDS.fromCompany].label,
    type: "select",
    required: true
  },
  // end referenzen-container fields

  [EDA_FORM_FIELDS.origin]: {
    name: EDA_FORM_FIELDS.origin,
    label: INPUT_LABELS[EDA_FORM_FIELDS.origin].label,
    type: "select",
    required: true,
    data: ORIGIN_OPTIONS
  },
  [EDA_FORM_FIELDS.medium]: {
    name: EDA_FORM_FIELDS.medium,
    label: INPUT_LABELS[EDA_FORM_FIELDS.medium].label,
    type: "select",
    required: true,
    data: MEDIUM_CHOICES
  },
  [EDA_FORM_FIELDS.validity]: {
    name: EDA_FORM_FIELDS.validity,
    label: INPUT_LABELS[EDA_FORM_FIELDS.validity].label,
    type: "date-range",
    required: true
  },
  [EDA_FORM_FIELDS.frequency]: {
    name: EDA_FORM_FIELDS.frequency,
    label: INPUT_LABELS[EDA_FORM_FIELDS.frequency].label,
    type: "select",
    required: true,
    data: FREQUENCY_OPTIONS
  },
  [EDA_FORM_FIELDS.formula]: {
    name: EDA_FORM_FIELDS.formula,
    label: INPUT_LABELS[EDA_FORM_FIELDS.formula].label,
    type: "text",
    required: true
  },
  [EDA_FORM_FIELDS.estimationProcedure]: {
    name: EDA_FORM_FIELDS.estimationProcedure,
    label: INPUT_LABELS[EDA_FORM_FIELDS.estimationProcedure].label,
    type: "select",
    required: true,
    data: ESTIMATION_PROCEDURE_OPTIONS
  },

  // estimation procedure fields
  [EDA_FORM_FIELDS.annualConsumption]: {
    name: EDA_FORM_FIELDS.annualConsumption,
    label: INPUT_LABELS[EDA_FORM_FIELDS.annualConsumption].label,
    type: "number",
    required: true
  },
  [EDA_FORM_FIELDS.constantLoadKw]: {
    name: EDA_FORM_FIELDS.constantLoadKw,
    label: INPUT_LABELS[EDA_FORM_FIELDS.constantLoadKw].label,
    type: "number",
    required: true
  },
  [EDA_FORM_FIELDS.numberOfConsumers]: {
    name: EDA_FORM_FIELDS.numberOfConsumers,
    label: INPUT_LABELS[EDA_FORM_FIELDS.numberOfConsumers].label,
    type: "number",
    required: true
  },
  [EDA_FORM_FIELDS.ratioOfEnergyData]: {
    name: EDA_FORM_FIELDS.ratioOfEnergyData,
    label: INPUT_LABELS[EDA_FORM_FIELDS.ratioOfEnergyData].label,
    type: "number",
    required: true
  },
  [EDA_FORM_FIELDS.referencedEdaIdentity]: {
    name: EDA_FORM_FIELDS.referencedEdaIdentity,
    label: INPUT_LABELS[EDA_FORM_FIELDS.referencedEdaIdentity].label,
    type: "select",
    required: true
  },
  [EDA_FORM_FIELDS.securityBuffer]: {
    name: EDA_FORM_FIELDS.securityBuffer,
    label: INPUT_LABELS[EDA_FORM_FIELDS.securityBuffer].label,
    type: "number",
    required: true
  },
  // end estimation procedure fields

  [EDA_FORM_FIELDS.label]: {
    name: EDA_FORM_FIELDS.label,
    label: INPUT_LABELS[EDA_FORM_FIELDS.label].label,
    type: "text",
    required: false
  },
  [EDA_FORM_FIELDS.stromsteuerpflichtig]: {
    name: EDA_FORM_FIELDS.stromsteuerpflichtig,
    label: INPUT_LABELS[EDA_FORM_FIELDS.stromsteuerpflichtig].label,
    type: "boolean",
    required: false
  },
  [EDA_FORM_FIELDS.tags]: {
    name: EDA_FORM_FIELDS.tags,
    label: INPUT_LABELS[EDA_FORM_FIELDS.tags].label,
    type: "multiselect",
    required: false,
    className: "tags",
    mt: "sm"
  }
} as const satisfies {
  [key in keyof EdaVersionFormValues]?: FormInputData<
    EdaVersionFormValues,
    key
  >;
};

export const ESTIMATION_PROCEDURE_FIELDS = {
  [ESTIMATION_PROCEDURES_EXAMPLE_MEASURING]: [
    "referenced_eda_identity",
    "security_buffer",
    "number_of_consumers"
  ],
  [ESTIMATION_PROCEDURES_PARITAL_RATIO]: [
    "referenced_eda_identity",
    "ratio_of_energy_data",
    "security_buffer"
  ],
  [ESTIMATION_PROCEDURES_NON_CALIBRATED_MEASURING]: [
    "referenced_eda_identity",
    "security_buffer"
  ],
  [ESTIMATION_PROCEDURES_TYPICAL_STANDARD_VALUES]: [
    "security_buffer",
    "number_of_consumers",
    "annual_consumption"
  ],
  [ESTIMATION_PROCEDURES_TYPICAL_STANDARD_VALUES_15_MIN]: [
    "security_buffer",
    "number_of_consumers",
    "annual_consumption"
  ],
  [ESTIMATION_PROCEDURES_CONSTANT_LOAD]: [
    "number_of_consumers",
    "constant_load_kw"
  ],
  [ESTIMATION_PROCEDURES_MISC]: ["computation", "frequency"]
} as const;
